<script>
  import { Meteor } from 'meteor/meteor';
  import { Tracker } from 'meteor/tracker';
  import { onMount } from 'svelte';
  import { InventoryCollection } from '../../../api/inventory/inventory';
  import RichTextEditor from '../../components/RichTextEditor.svelte';
  import { callMethod } from './managementMethods';

  let { onActionMessage = () => {} } = $props();

  let vehicles = $state([]);
  let editorRenderKey = $state(0);
  let rightPaneMode = $state('form');
  let mediaEditorInventoryId = $state('');
  let selectedInventoryId = $state('');
  let youtubeVideoUrl = $state('');
  let paneVehicle = $derived(vehicles.find((entry) => entry._id === mediaEditorInventoryId));
  let canOpenMediaPane = $derived(Boolean(paneVehicle || selectedInventoryId));
  let activeMediaVehicle = $derived(paneVehicle || vehicles.find((entry) => entry._id === selectedInventoryId));

  let inventoryForm = $state({
    title: '',
    type: '',
    price: '',
    cost: '',
    vin: '',
    make: '',
    model: '',
    year: '',
    mileage: '',
    color: '',
    drive: '',
    transmission: '',
    trim: '',
    engine: '',
    interiorColor: '',
    upholstery: '',
    condition: '',
    description: '',
  });

  function setActionMessage(message) {
    onActionMessage(message);
  }

  function resetInventoryForm() {
    inventoryForm = {
      title: '',
      type: '',
      price: '',
      cost: '',
      vin: '',
      make: '',
      model: '',
      year: '',
      mileage: '',
      color: '',
      drive: '',
      transmission: '',
      trim: '',
      engine: '',
      interiorColor: '',
      upholstery: '',
      condition: '',
      description: '',
    };
    editorRenderKey += 1;
  }

  function cancelInventoryEdit() {
    selectedInventoryId = '';
    rightPaneMode = 'form';
    resetInventoryForm();
    setActionMessage('Edit cancelled.');
  }

  function openPaneMode(mode) {
    if (mode === 'form') {
      rightPaneMode = 'form';
      return;
    }

    const targetInventoryId = selectedInventoryId || mediaEditorInventoryId;
    if (!targetInventoryId) {
      setActionMessage('Select a vehicle row first.');
      return;
    }

    mediaEditorInventoryId = targetInventoryId;
    rightPaneMode = mode;
  }

  onMount(() => {
    const inventorySub = Meteor.subscribe('mgmt.inventory.compact');
    const computation = Tracker.autorun(() => {
      vehicles = InventoryCollection.find({}, { sort: { created: -1 } }).fetch();
    });

    return () => {
      computation.stop();
      inventorySub.stop();
    };
  });

  async function saveInventory(event) {
    event.preventDefault();

    try {
      if (selectedInventoryId) {
        await callMethod('inventory.update', { inventoryId: selectedInventoryId, payload: inventoryForm });
        setActionMessage('Inventory item updated.');
      } else {
        await callMethod('inventory.create', inventoryForm);
        setActionMessage('Inventory item created.');
      }

      resetInventoryForm();
      selectedInventoryId = '';
      rightPaneMode = 'form';
    } catch (error) {
      setActionMessage(error.reason || 'Inventory save failed.');
    }
  }

  function editInventory(item) {
    selectedInventoryId = item._id;
    mediaEditorInventoryId = item._id;
    rightPaneMode = 'form';
    inventoryForm = {
      title: item.title || '',
      type: item.type || '',
      price: item.price ?? '',
      cost: item.cost ?? '',
      vin: item.vin || '',
      make: item.make,
      model: item.model,
      year: item.year ?? '',
      mileage: item.mileage ?? '',
      color: item.color || '',
      drive: item.drive || '',
      transmission: item.transmission || '',
      trim: item.trim || '',
      engine: item.engine || '',
      interiorColor: item.interiorColor || '',
      upholstery: item.upholstery || '',
      condition: item.condition || '',
      description: item.description || '',
    };
    editorRenderKey += 1;
  }

  function getVisibilityState(item) {
    if (item.deleted) return 'hidden';
    if (item.featured) return 'featured';
    return 'showing';
  }

  async function cycleVisibility(item) {
    try {
      await callMethod('inventory.cycleVisibility', item._id);
      const current = getVisibilityState(item);
      setActionMessage(current === 'hidden' ? 'Vehicle is now visible.' : current === 'showing' ? 'Vehicle is now featured.' : 'Vehicle hidden.');
    } catch (error) {
      setActionMessage(error.reason || 'Could not update visibility.');
    }
  }

  async function markSold(item) {
    try {
      await callMethod('inventory.markSold', item._id);
      setActionMessage(item.sold ? 'Inventory item marked as available.' : 'Inventory item marked as sold.');
    } catch (error) {
      setActionMessage(error.reason || 'Could not update sold status.');
    }
  }

  async function deleteInventory(item) {
    try {
      await callMethod('inventory.softDelete', item._id);
      if (mediaEditorInventoryId === item._id || selectedInventoryId === item._id) {
        mediaEditorInventoryId = '';
        if (selectedInventoryId === item._id) {
          selectedInventoryId = '';
          resetInventoryForm();
        }
        rightPaneMode = 'form';
      }
    } catch (error) {
      setActionMessage(error.reason || 'Could not delete inventory.');
    }
  }

  function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Could not read file.'));
      reader.readAsDataURL(file);
    });
  }

  async function uploadMediaFiles(event, item, type) {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setActionMessage(`Uploading ${files.length} ${type}${files.length === 1 ? '' : 's'}...`);

    try {
      for (const file of files) {
        const dataUrl = await readFileAsDataUrl(file);
        if (type === 'photo') {
          await callMethod('inventory.media.uploadPhoto', {
            inventoryId: item._id,
            fileName: file.name,
            dataUrl,
          });
        } else {
          await callMethod('inventory.media.uploadVideo', {
            inventoryId: item._id,
            fileName: file.name,
            dataUrl,
          });
        }
      }
      setActionMessage(`${files.length} ${type}${files.length === 1 ? '' : 's'} uploaded.`);
    } catch (error) {
      setActionMessage(error.reason || `${type} upload failed.`);
    } finally {
      event.target.value = '';
    }
  }

  async function removeMedia(item, mediaType, mediaId) {
    try {
      await callMethod('inventory.media.remove', { inventoryId: item._id, mediaType, mediaId });
      setActionMessage(`${mediaType === 'photos' ? 'Photo' : 'Video'} removed.`);
    } catch (error) {
      setActionMessage(error.reason || 'Could not remove media.');
    }
  }

  async function setPrimaryMedia(item, mediaType, mediaId) {
    try {
      await callMethod('inventory.media.setPrimary', { inventoryId: item._id, mediaType, mediaId });
      setActionMessage(`${mediaType === 'photos' ? 'Photo' : 'Video'} marked primary.`);
    } catch (error) {
      setActionMessage(error.reason || 'Could not set primary media.');
    }
  }

  async function addYouTubeVideo(item) {
    const url = youtubeVideoUrl.trim();
    if (!url) {
      setActionMessage('Enter a YouTube URL first.');
      return;
    }

    try {
      await callMethod('inventory.media.addVideoLink', { inventoryId: item._id, url });
      youtubeVideoUrl = '';
      setActionMessage('YouTube video added.');
    } catch (error) {
      setActionMessage(error.reason || 'Could not add YouTube video.');
    }
  }
</script>

<div class="mgmt-grid">
  <div>
    <h2>Inventory List</h2>
    <table>
      <thead>
        <tr><th>Title</th><th>Price</th><th>VIN</th><th>Status</th><th>Flags</th></tr>
      </thead>
      <tbody>
        {#each vehicles as vehicle (vehicle._id)}
          <tr
            class:selected-row={selectedInventoryId === vehicle._id}
            class:editing-row={selectedInventoryId === vehicle._id}
            onclick={() => editInventory(vehicle)}
          >
            <td>{vehicle.title || [vehicle.make, vehicle.model].filter(Boolean).join(' ') || '-'}</td>
            <td>{vehicle.price ?? '-'}</td>
            <td>{vehicle.vin || '-'}</td>
            <td>{vehicle.sold ? 'Sold' : vehicle.deleted ? 'Hidden' : vehicle.featured ? 'Featured' : 'Live'}</td>
            <td class="row-flags">
              <button
                type="button"
                class="flag-btn"
                class:flag-showing={getVisibilityState(vehicle) === 'showing'}
                class:flag-featured={getVisibilityState(vehicle) === 'featured'}
                class:flag-hidden={getVisibilityState(vehicle) === 'hidden'}
                title={
                  getVisibilityState(vehicle) === 'hidden'
                    ? 'Hidden: click to Show'
                    : getVisibilityState(vehicle) === 'showing'
                      ? 'Showing: click to Feature'
                      : 'Featured: click to Hide'
                }
                onclick={(event) => {
                  event.stopPropagation();
                  cycleVisibility(vehicle);
                }}
              >
                !
              </button>
              <button
                type="button"
                class="flag-btn sold-flag"
                class:flag-active={Boolean(vehicle.sold)}
                title={vehicle.sold ? 'Mark available' : 'Mark sold'}
                onclick={(event) => {
                  event.stopPropagation();
                  markSold(vehicle);
                }}
              >
                $
              </button>
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>

  <div>
    <div class="pane-tabs">
      <button class:active={rightPaneMode === 'form'} type="button" onclick={() => openPaneMode('form')}>Details</button>
      <button
        class:active={rightPaneMode === 'photos'}
        type="button"
        onclick={() => openPaneMode('photos')}
        disabled={!canOpenMediaPane}
      >
        Photos Editor
      </button>
      <button
        class:active={rightPaneMode === 'videos'}
        type="button"
        onclick={() => openPaneMode('videos')}
        disabled={!canOpenMediaPane}
      >
        Videos Editor
      </button>
    </div>

    {#if rightPaneMode === 'form'}
      <h2>{selectedInventoryId ? 'Edit Inventory' : 'Create Inventory'}</h2>
      <form class="form-grid" onsubmit={saveInventory}>
        <label>Title<input bind:value={inventoryForm.title} /></label>
        <label>Type<input bind:value={inventoryForm.type} /></label>
        <label>Price<input type="number" bind:value={inventoryForm.price} /></label>
        <label>Cost<input type="number" bind:value={inventoryForm.cost} /></label>
        <label>VIN<input bind:value={inventoryForm.vin} /></label>
        <label>Make<input required bind:value={inventoryForm.make} /></label>
        <label>Model<input required bind:value={inventoryForm.model} /></label>
        <label>Year<input type="number" bind:value={inventoryForm.year} /></label>
        <label>Mileage<input type="number" bind:value={inventoryForm.mileage} /></label>
        <label>Color<input bind:value={inventoryForm.color} /></label>
        <label>Drive<input bind:value={inventoryForm.drive} /></label>
        <label>Transmission<input bind:value={inventoryForm.transmission} /></label>
        <label>Trim<input bind:value={inventoryForm.trim} /></label>
        <label>Engine<input bind:value={inventoryForm.engine} /></label>
        <label>Interior Color<input bind:value={inventoryForm.interiorColor} /></label>
        <label>Upholstery<input bind:value={inventoryForm.upholstery} /></label>
        <label>Condition<input bind:value={inventoryForm.condition} /></label>
        <div class="full">
          <p class="editor-label">Description</p>
          {#key editorRenderKey}
            <RichTextEditor bind:value={inventoryForm.description} />
          {/key}
        </div>
        <div class="form-actions full">
          <button type="submit">{selectedInventoryId ? 'Update' : 'Create'}</button>
          {#if selectedInventoryId}
            <button type="button" class="secondary-btn" onclick={cancelInventoryEdit}>Cancel Edit</button>
          {/if}
        </div>
      </form>
    {:else}
      <h2>{rightPaneMode === 'photos' ? 'Photos Editor' : 'Videos Editor'}</h2>
      {#if activeMediaVehicle}
        <p class="media-editor-subtitle">
          {activeMediaVehicle.title || [activeMediaVehicle.make, activeMediaVehicle.model].filter(Boolean).join(' ') || 'Selected vehicle'}
        </p>
        <div class="media-upload-row">
          <label class="media-upload-picker">
            <span>{rightPaneMode === 'photos' ? 'Add Photos' : 'Add Videos'}</span>
            <input
              type="file"
              accept={rightPaneMode === 'photos' ? 'image/*' : 'video/*'}
              multiple
              onchange={(event) => uploadMediaFiles(event, activeMediaVehicle, rightPaneMode === 'photos' ? 'photo' : 'video')}
            />
          </label>
          {#if rightPaneMode === 'videos'}
            <div class="video-link-input">
              <label>
                <span>YouTube URL</span>
                <input bind:value={youtubeVideoUrl} placeholder="https://www.youtube.com/watch?v=..." />
              </label>
              <button type="button" class="secondary-btn" onclick={() => addYouTubeVideo(activeMediaVehicle)}>
                Add Link
              </button>
            </div>
          {/if}
        </div>

        {@const mediaItems = rightPaneMode === 'photos' ? activeMediaVehicle.photos || [] : activeMediaVehicle.videos || []}
        {#if !mediaItems.length}
          <p>No {rightPaneMode} uploaded yet.</p>
        {:else}
          <div class="media-item-grid">
            {#each mediaItems as media, index (media.id)}
              <article class="media-item-card">
                <div class="media-preview">
                  {#if rightPaneMode === 'photos'}
                    <img
                      src={media.variants?.card || media.variants?.detail || media.variants?.thumb || media.original}
                      alt="Vehicle"
                    />
                  {:else if media.poster}
                    <img src={media.poster} alt="Video preview" />
                  {:else if media.outputs?.sd}
                    <video controls preload="metadata" src={media.outputs.sd}>
                      <track kind="captions" />
                    </video>
                  {:else}
                    <p>Video unavailable</p>
                  {/if}
                </div>

                <p class="media-item-meta">
                  {index === 0 ? 'Primary' : `Position ${index + 1}`}
                  {#if rightPaneMode === 'videos' && media.status}
                    · {media.status}
                  {/if}
                  {#if rightPaneMode === 'videos' && media.source === 'youtube'}
                    · YouTube
                  {/if}
                </p>

                <div class="media-item-actions">
                  <button
                    type="button"
                    class="secondary-btn"
                    onclick={() => setPrimaryMedia(activeMediaVehicle, rightPaneMode, media.id)}
                    disabled={index === 0}
                  >
                    Make Primary
                  </button>
                  <button type="button" class="danger-btn" onclick={() => removeMedia(activeMediaVehicle, rightPaneMode, media.id)}>
                    Delete
                  </button>
                </div>
              </article>
            {/each}
          </div>
        {/if}
      {:else}
        <p>Select a vehicle row to manage media.</p>
      {/if}
    {/if}
  </div>
</div>
